import {
	Button,
	Loader,
	Message,
	Modal,
	ModalHeader,
} from '@mairistem/composants';
import type { JournalBordereau } from '@mairistem/comptabilite';
import React, { useEffect, useState } from 'react';
import BordereauEtat from '../../../../../constants/liquidation/bordereaux/BordereauEtat';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import {
	genererFluxPesBordereau,
	PesErreur,
	telechargerFichier,
} from '../../../../../services/pes/pesAller';
import getMessageErreur from '../../../../../utils/getMessageErreur';

interface EmettreFlux360ModalProps {
	onChange: (data: BordereauWithState) => void;
	onClose: VoidFunction;
	bordereau: BordereauWithState;
	visible: boolean;
	/** Relance : une confirmation est demandée avant de générer le flux. */
	isRelance?: boolean;
	isSuperUser?: boolean;
}

const formatDate = (date: string, options: Intl.DateTimeFormatOptions) =>
	new Date(date).toLocaleString('fr-FR', options);

const EmettreFlux360Modal = ({
	onChange,
	onClose,
	bordereau,
	visible,
	isRelance = false,
	isSuperUser = false,
}: EmettreFlux360ModalProps) => {
	const { isValidated, validationComponent } = useModalSuperUserValidation();
	const [confirme, setConfirme] = useState(!isRelance);
	const [loading, setLoading] = useState(!isRelance);
	const [termine, setTermine] = useState(false);
	const [erreurs, setErreurs] = useState<string[]>([]);
	const [avertissements, setAvertissements] = useState<string[]>([]);

	const lastJournalPES: JournalBordereau | undefined = [
		...(bordereau.journauxBordereau ?? []),
	]
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
		.find(
			(journal) => journal.etat === BordereauEtat.TRAITEMENT_PES_EN_INSTANCE,
		);

	// biome-ignore lint/correctness/useExhaustiveDependencies: génération lancée une seule fois, à la confirmation
	useEffect(() => {
		if (!confirme) {
			return;
		}
		setLoading(true);
		genererFluxPesBordereau(
			Number(bordereau.identifiant),
			bordereau.identifiantEntite,
			{ mode360: true },
		)
			.then((flux) => {
				const messagesAvertissement = flux.avertissements.map(
					({ message }) => message,
				);
				setAvertissements(messagesAvertissement);
				// conforme vaut false en mode debug : le flux ne doit pas être transmis.
				if (!flux.conforme || flux.anomalies.length > 0) {
					const anomalies = flux.anomalies.map(({ message }) => message);
					setErreurs(
						anomalies.length > 0
							? anomalies
							: ['Le flux PES généré est non conforme.'],
					);
					return;
				}
				telechargerFichier(flux.fichier, flux.nomFichier);
				setTermine(true);
				onChange(bordereau);
				if (messagesAvertissement.length === 0) {
					onClose();
				}
			})
			.catch((error) => {
				setErreurs(
					error instanceof PesErreur && error.messages.length > 0
						? error.messages
						: [
								getMessageErreur(
									error,
									isRelance
										? 'Impossible de relancer le flux PES 360'
										: "Impossible d'émettre le flux PES 360",
								),
							],
				);
			})
			.finally(() => setLoading(false));
	}, [confirme]);

	return (
		<Modal
			open={visible}
			onClose={loading ? undefined : onClose}
			closeIcon={!loading}
			data-testid="emettreFlux360Modal"
		>
			<ModalHeader>
				{isRelance ? 'Relancer' : 'Émettre'} le flux 360 du bordereau n°
				{bordereau.numero}
			</ModalHeader>
			<Modal.Content>
				{!confirme && (
					<>
						<p>
							{!lastJournalPES ? (
								"Êtes-vous sûr de vouloir relancer l'émission du flux PES 360 ?"
							) : (
								<>
									Ce bordereau fait l’objet d’un traitement PES effectué par{' '}
									{lastJournalPES.utilisateur?.prenom}{' '}
									{lastJournalPES.utilisateur?.nom} le{' '}
									{formatDate(lastJournalPES.date, {
										year: 'numeric',
										month: '2-digit',
										day: '2-digit',
									})}
									{' à '}
									{formatDate(lastJournalPES.date, {
										hour: '2-digit',
										minute: '2-digit',
									})}
								</>
							)}
						</p>
						{isSuperUser && validationComponent}
					</>
				)}
				{loading && (
					<Loader active inline="centered">
						Génération du flux PES en cours…
					</Loader>
				)}
				{erreurs.length > 0 && (
					<Message negative data-testid="emettreFlux360ModalErreurs">
						<Message.Header content="Le flux PES n'a pas pu être émis" />
						<Message.List items={erreurs} />
					</Message>
				)}
				{termine && (
					<Message positive content="Le flux PES a été généré et téléchargé." />
				)}
				{avertissements.length > 0 && (
					<Message warning data-testid="emettreFlux360ModalAvertissements">
						<Message.Header content="Avertissements" />
						<Message.List items={avertissements} />
					</Message>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button
					disabled={loading}
					onClick={onClose}
					data-testid="emettreFlux360ModalClose"
				>
					{confirme ? 'Fermer' : 'Annuler'}
				</Button>
				{!confirme && (
					<Button
						primary
						disabled={isSuperUser && !isValidated}
						onClick={() => setConfirme(true)}
						data-testid="emettreFlux360ModalConfirm"
					>
						Confirmer
					</Button>
				)}
			</Modal.Actions>
		</Modal>
	);
};

export default EmettreFlux360Modal;
